/** Haversine distance in meters between two lat/lon points */
export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000; // Earth radius meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

/** Scale lat/lon coordinates to fit within a canvas/SVG area, preserving aspect ratio */
export function scaleCoordsToCanvas(
  wells: Array<{ latitude: number | null; longitude: number | null }>,
  width: number,
  height: number,
  padding: number,
): Array<{ x: number; y: number }> {
  const valid = wells.filter(w => w.latitude != null && w.longitude != null);
  if (valid.length < 2) {
    // Fallback: grid layout
    const cols = Math.ceil(Math.sqrt(wells.length));
    const cellW = (width - padding * 2) / Math.max(cols, 1);
    const cellH = (height - padding * 2) / Math.max(Math.ceil(wells.length / cols), 1);
    return wells.map((_, i) => ({
      x: padding + (i % cols) * cellW + cellW / 2,
      y: padding + Math.floor(i / cols) * cellH + cellH / 2,
    }));
  }

  const lats = wells.map(w => w.latitude ?? valid[0].latitude!);
  const lons = wells.map(w => w.longitude ?? valid[0].longitude!);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLon = Math.min(...lons), maxLon = Math.max(...lons);
  const latRange = maxLat - minLat || 0.0001;
  const lonRange = maxLon - minLon || 0.0001;

  // Cosine correction for longitude
  const midLat = (minLat + maxLat) / 2;
  const cosLat = Math.cos(midLat * Math.PI / 180);
  const geoW = lonRange * cosLat;
  const geoH = latRange;

  const usableW = width - padding * 2;
  const usableH = height - padding * 2;
  const geoAspect = geoW / geoH;
  const canvasAspect = usableW / usableH;

  let scaleW: number, scaleH: number, offX = 0, offY = 0;
  if (geoAspect > canvasAspect) {
    scaleW = usableW;
    scaleH = usableW / geoAspect;
    offY = (usableH - scaleH) / 2;
  } else {
    scaleH = usableH;
    scaleW = usableH * geoAspect;
    offX = (usableW - scaleW) / 2;
  }

  const margin = 0.06;
  return wells.map((w) => {
    const lat = w.latitude ?? valid[0].latitude!;
    const lon = w.longitude ?? valid[0].longitude!;
    const nx = lonRange > 0 ? (lon - minLon) / lonRange : 0.5;
    const ny = latRange > 0 ? (maxLat - lat) / latRange : 0.5; // invert Y (north up)
    return {
      x: padding + offX + margin * scaleW + nx * scaleW * (1 - 2 * margin),
      y: padding + offY + margin * scaleH + ny * scaleH * (1 - 2 * margin),
    };
  });
}

/** Compute bearing in degrees from point 1 to point 2 */
export function computeBearing(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const y = Math.sin(dLon) * Math.cos(lat2 * Math.PI / 180);
  const x = Math.cos(lat1 * Math.PI / 180) * Math.sin(lat2 * Math.PI / 180) -
            Math.sin(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.cos(dLon);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

/** Build NxN distance matrix (meters) for wells with coordinates */
export function buildProximityMatrix(
  wells: Array<{ latitude: number | null; longitude: number | null }>,
): number[][] {
  return wells.map((a) =>
    wells.map((b) => {
      if (a.latitude == null || a.longitude == null || b.latitude == null || b.longitude == null) return Infinity;
      return haversineDistance(a.latitude, a.longitude, b.latitude, b.longitude);
    })
  );
}
